import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const AdminPanel = () => {
  const [reports, setReports] = useState([]);
  const [isSoundEnabled, setIsSoundEnabled] = useState(false);
  const prevReportCount = useRef(0);
  const isFirstLoad = useRef(true);

  // Ses dosyasını tanımlıyoruz (Standart dijital alarm sesi)
  const alarmSound = new Audio('https://actions.google.com/sounds/v1/alarms/digital_watch_alarm_long.ogg');

  useEffect(() => {
    const q = query(collection(db, "ihbarlar"), orderBy("zaman", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rpts = [];
      snapshot.forEach((document) => {
        rpts.push({ id: document.id, ...document.data() });
      });
      
      // SESLİ ALARM MANTIĞI: Eğer yeni bir rapor eklendiyse ve durum bekliyorsa ses çal
      if (!isFirstLoad.current && rpts.length > prevReportCount.current) {
        const latestReport = rpts[0];
        if (latestReport.durum === 'Bekliyor' && isSoundEnabled) {
          alarmSound.play().catch(e => console.log("Tarayıcı sesi engelledi, etkileşim gerekiyor."));
          
          MySwal.fire({
            toast: true, position: 'top-end', icon: 'warning',
            title: 'Yeni Acil İhbar Alındı!',
            showConfirmButton: false, timer: 4000, timerProgressBar: true
          });
        }
      }
      
      setReports(rpts);
      prevReportCount.current = rpts.length;
      isFirstLoad.current = false;
    });
    return () => unsubscribe();
  }, [isSoundEnabled]);

  // --- İSTATİSTİKLERİ HESAPLAMA ---
  const totalReports = reports.length;
  const pendingReports = reports.filter(r => r.durum === 'Bekliyor').length;
  const resolvedReports = reports.filter(r => r.durum === 'Müdahale Edildi').length;

  const handleStatusChange = async (id, newStatus) => {
    try {
      const reportRef = doc(db, "ihbarlar", id);
      await updateDoc(reportRef, { durum: newStatus });
      const Toast = MySwal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
      Toast.fire({ icon: 'success', title: 'Durum güncellendi' });
    } catch (error) {
      MySwal.fire('Hata', 'Güncelleme yapılamadı.', 'error');
    }
  };

  const handleDelete = async (id) => {
    const confirm = await MySwal.fire({
      title: 'İhbarı Sil', text: "Bu kayıt veritabanından kalıcı olarak silinecektir. Onaylıyor musunuz?", icon: 'warning', showCancelButton: true, confirmButtonColor: '#e11d48', cancelButtonColor: '#64748b', confirmButtonText: 'Evet, Sil', cancelButtonText: 'Vazgeç', customClass: { container: 'font-sans' }
    });

    if (confirm.isConfirmed) {
      await deleteDoc(doc(db, "ihbarlar", id));
      MySwal.fire({ title: 'Silindi!', text: 'İhbar kayıt dışı bırakıldı.', icon: 'success', confirmButtonColor: '#0f172a' });
    }
  };

  const showUserDetails = (report) => {
    MySwal.fire({
      title: 'İhbarcı & Konum Detayı',
      html: `
        <div class="text-left bg-slate-50 p-4 rounded-xl border border-slate-200 mt-4 font-sans text-sm space-y-3">
            <div class="border-b border-slate-200 pb-2"><span class="text-slate-400 font-bold text-xs uppercase block">Ad Soyad:</span><span class="font-bold text-slate-800 text-base">${report.adSoyad || 'Bilinmiyor'}</span></div>
            <div class="border-b border-slate-200 pb-2"><span class="text-slate-400 font-bold text-xs uppercase block">İletişim Numarası:</span><span class="font-bold text-slate-800">${report.telefon || 'Belirtilmemiş'}</span></div>
            <div class="border-b border-slate-200 pb-2"><span class="text-slate-400 font-bold text-xs uppercase block">E-Posta:</span><span class="font-medium text-slate-600">${report.kullaniciEmail || 'Telefon ile Giriş'}</span></div>
            <div class="pt-1"><span class="text-slate-400 font-bold text-xs uppercase block">Kesin Koordinat:</span><span class="font-bold text-blue-600 tracking-tight">${report.konum?.enlem.toFixed(6)}, ${report.konum?.boylam.toFixed(6)}</span></div>
        </div>
      `,
      icon: 'info', confirmButtonText: 'Anlaşıldı', confirmButtonColor: '#0f172a', customClass: { container: 'font-sans' }
    });
  };

  // --- YENİ: YOL TARİFİ ALMA FONKSİYONU ---
  const handleGetDirections = (report) => {
    if (report.konum && report.konum.enlem && report.konum.boylam) {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${report.konum.enlem},${report.konum.boylam}`;
        window.open(url, '_blank');
    } else {
        MySwal.fire('Hata', 'Bu ihbarın konum verisi hatalı.', 'error');
    }
  };

  const handleShare = async (report) => {
    // URL düzeltildi: En standart Google Maps formatı
    const mapsLink = `https://maps.google.com/?q=${report.konum?.enlem},${report.konum?.boylam}`;
    const reportTime = report.zaman ? new Date(report.zaman.seconds * 1000).toLocaleString('tr-TR') : 'Bilinmiyor';
    
    const shareText = `🚨 *ACİL DURUM İHBARI* (${report.tip.toUpperCase()})\n\n🕒 *Zaman:* ${reportTime}\n📍 *Konum:* ${mapsLink}\n👤 *Bildiren:* ${report.adSoyad || 'Bilinmiyor'}\n📞 *Telefon:* ${report.telefon || 'Bilinmiyor'}\n📸 *Görsel Kanıt:* ${report.medyaUrl || 'Yok'}`;

    const encodedText = encodeURIComponent(shareText);
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobileDevice && navigator.share) {
      try { await navigator.share({ title: 'Afet İhbarı', text: shareText }); } catch (err) { console.log("Paylaşım iptal edildi."); }
    } else {
      window.open(`https://wa.me/?text=${encodedText}`, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 w-full h-full overflow-y-auto bg-slate-100 font-sans antialiased text-slate-800 z-50">
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 min-h-full">
        
        {/* ÜST BAŞLIK VE ALARM BUTONU */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-6 bg-white p-8 rounded-[32px] shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-slate-100">
          <div className="flex items-center gap-5">
            <div className="bg-slate-800 p-4 rounded-2xl shadow-lg shadow-slate-200 text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase italic">Denetim Paneli</h1>
              <p className="text-slate-500 font-bold text-sm mt-1 uppercase tracking-widest">Saha Yönetimi ve İstatistikler</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 w-full md:w-auto">
            <button 
              onClick={() => setIsSoundEnabled(!isSoundEnabled)} 
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold transition-all shadow-sm ${isSoundEnabled ? 'bg-red-100 text-red-600 border-2 border-red-200' : 'bg-slate-100 text-slate-500 border-2 border-slate-200 hover:bg-slate-200'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isSoundEnabled ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />}
              </svg>
              {isSoundEnabled ? 'Alarm Açık' : 'Alarm Kapalı'}
            </button>
            <Link to="/" className="flex-1 md:flex-none text-center bg-slate-800 border-2 border-slate-800 text-white px-8 py-4 rounded-2xl font-bold hover:bg-black transition-all shadow-lg active:scale-95">
              Haritaya Dön
            </Link>
          </div>
        </div>

        {/* YENİ: İSTATİSTİK KARTLARI */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Bekleyen İhbar</p>
                    <p className="text-4xl font-black text-amber-500">{pendingReports}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-2xl text-amber-500">⏳</div>
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Müdahale Edildi</p>
                    <p className="text-4xl font-black text-emerald-500">{resolvedReports}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-2xl text-emerald-500">✅</div>
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Toplam Kayıt</p>
                    <p className="text-4xl font-black text-slate-800">{totalReports}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl text-slate-600">📊</div>
            </div>
        </div>

        {/* TABLO */}
        <div className="bg-white rounded-[32px] shadow-[0_10px_40px_rgba(0,0,0,0.04)] overflow-hidden border border-slate-100 mb-20">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-6 font-black uppercase text-[10px] tracking-widest text-slate-400">Zaman / Tip</th>
                  <th className="p-6 font-black uppercase text-[10px] tracking-widest text-slate-400">Bildiren Ad Soyad</th>
                  <th className="p-6 font-black uppercase text-[10px] tracking-widest text-slate-400 text-center">Görsel Kanıt</th>
                  <th className="p-6 font-black uppercase text-[10px] tracking-widest text-slate-400 text-center">Durum Güncelle</th>
                  <th className="p-6 font-black uppercase text-[10px] tracking-widest text-slate-400 text-right">Ekiplerle Paylaş</th>
                </tr>
              </thead>
              <tbody>
                {reports.length === 0 ? (
                  <tr><td colSpan="5" className="p-20 text-center text-slate-400 font-bold uppercase tracking-widest">Henüz bir veri girişi bulunmuyor.</td></tr>
                ) : (
                  reports.map(report => (
                    <tr key={report.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="p-6">
                        <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-lg text-xs font-bold inline-block border border-slate-200">{report.zaman ? new Date(report.zaman.seconds * 1000).toLocaleString('tr-TR') : '-'}</span>
                        <p className={`text-[11px] font-black uppercase mt-2 ${report.tip === 'Alev' ? 'text-red-600' : 'text-slate-500'}`}>{report.tip === 'Alev' ? '🔥 ALEV İHBARI' : '💨 DUMAN İHBARI'}</p>
                      </td>
                      <td className="p-6">
                        <button onClick={() => showUserDetails(report)} className="flex items-center gap-3 text-left group">
                            <div className="bg-slate-100 p-2.5 rounded-xl group-hover:bg-slate-800 group-hover:text-white transition-all"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg></div>
                            <div>
                                <p className="text-sm font-black text-slate-800 group-hover:text-slate-900 truncate max-w-[160px] capitalize">{report.adSoyad || (report.kullaniciEmail ? report.kullaniciEmail.split('@')[0] : 'Anonim Kullanıcı')}</p>
                                <p className="text-[10px] font-bold text-slate-400 mt-0.5 uppercase tracking-tighter">Detaylı Bilgi İçin Tıkla</p>
                            </div>
                        </button>
                      </td>
                      <td className="p-6 text-center">
                        <a href={report.medyaUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-slate-800 text-white px-5 py-2.5 rounded-xl text-[11px] font-bold uppercase hover:bg-black transition-all shadow-md active:scale-95">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          GÖRSELİ AÇ
                        </a>
                      </td>
                      <td className="p-6 text-center">
                        <select value={report.durum || 'Bekliyor'} onChange={(e) => handleStatusChange(report.id, e.target.value)} className={`text-[10px] font-black p-3 rounded-xl border-2 outline-none transition-all cursor-pointer shadow-sm w-44 uppercase ${report.durum === 'Bekliyor' ? 'border-amber-200 bg-amber-50 text-amber-700' : report.durum === 'Ekiplere İletildi' ? 'border-blue-200 bg-blue-50 text-blue-700' : report.durum === 'Müdahale Edildi' ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                          <option value="Bekliyor">⏳ BEKLEYEN</option>
                          <option value="Ekiplere İletildi">🚑 EKİPLERE İLETİLDİ</option>
                          <option value="Müdahale Edildi">🚒 MÜDAHALE EDİLDİ</option>
                          <option value="Asılsız">❌ ASILSIZ İHBAR</option>
                        </select>
                      </td>
                      <td className="p-6 text-right flex items-center justify-end gap-2">
                        {/* YENİ: YOL TARİFİ BUTONU */}
                        <button onClick={() => handleGetDirections(report)} className="bg-emerald-500 text-white p-3 rounded-xl hover:bg-emerald-600 transition-all shadow-lg active:scale-90" title="Yol Tarifi Al">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                        </button>
                        
                        <button onClick={() => handleShare(report)} className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 transition-all shadow-lg active:scale-90" title="Bilgileri Paylaş">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                        </button>
                        <button onClick={() => handleDelete(report.id)} className="bg-slate-100 text-slate-400 p-3 rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm active:scale-90" title="İhbarı Sil">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;